import { useEffect, useRef } from "react";
import { Link, useLoaderData, useSearchParams, type LoaderFunctionArgs } from "react-router";
import { Loading, times } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import type { ConnectorSummary } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext, sessionContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Callout, ConfluenceIcon, ConnectorIcon, DiscordIcon, GitHubIcon, JiraIcon, LinearIcon, NotionIcon, PageHeader, SlackIcon, TeamsIcon, useToast, Skeleton, type BadgeTone, type IconProps } from "../../ui/index.js";

/**
 * Settings: the platforms this workspace's agent works in (D-0018, D-0019) or reads from (Notion).
 * The page renders whatever the control plane lists, so a new platform needs no change here.
 * This is the directory; each platform's own page is `connector.tsx`.
 */

export function connectorsLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const me = context.get(sessionContext);
  const workspace = context.get(apiContext).workspace(ws.id);
  // Installing and removing need integration:manage, which owners and admins hold.
  const data = Promise.all([workspace.listConnectors(), workspace.get()]).then(([page, detail]) => {
    const role = detail.members.find((m) => m.userId === me?.actor.id)?.role;
    return { connectors: page.items, canManage: role === "owner" || role === "admin", viewerId: me?.actor.id ?? "" };
  });
  // The page's buttons call the same client the loader used, so a test's fake serves both.
  return { workspace, workspaceSlug: ws.slug, data: deferred(data) };
}

// Drawn when a connector's manifest names no logo.
const LOGOS: Record<string, (props: IconProps) => React.JSX.Element> = { slack: SlackIcon, github: GitHubIcon, notion: NotionIcon, confluence: ConfluenceIcon, linear: LinearIcon, jira: JiraIcon, discord: DiscordIcon, teams: TeamsIcon };
// Outcomes that are not failures, and the toast each one gets.
const OUTCOMES = { installed: "success", linked: "success", updated: "neutral", requested: "neutral" } as const;
const REASONS = new Set(["denied", "forbidden", "signed_out", "already_bound", "failed"]);

export function ConnectorLogo({ connector, size = 40 }: { connector: Pick<ConnectorSummary, "provider" | "logoUrl">; size?: number }) {
  const Logo = LOGOS[connector.provider] ?? ConnectorIcon;
  const inner = Math.round(size * 0.55);
  return (
    <span className="dt-connector-logo" style={{ width: size, height: size }}>
      {connector.logoUrl ? <img src={connector.logoUrl} alt="" width={inner} height={inner} /> : <Logo size={inner} />}
    </span>
  );
}

/** A manifest's text in the current language; English when it was not written in that one. */
export function localized(text: Record<string, string> | undefined, locale: string): string | undefined {
  return text?.[locale] ?? text?.en;
}

/** One word for where a connector stands. Null is plain "not installed": nothing to say on a card. */
export function connectorStatus(connector: ConnectorSummary): { key: MessageKey; tone: BadgeTone } | null {
  if (connector.installation) return connector.installation.suspended ? { key: "connectors.status.suspended", tone: "warning" } : { key: "connectors.status.enabled", tone: "success" };
  if (!connector.configured) return { key: "connectors.status.unavailable", tone: "neutral" };
  return connector.pending ? { key: "connectors.status.requested", tone: "info" } : null;
}

/** The provider sent the browser back with the outcome in the query: say it once, then drop it. */
export function useInstallOutcome(connectors: ConnectorSummary[]) {
  const [params, setParams] = useSearchParams();
  const { t } = useI18n();
  const toast = useToast();
  const told = useRef(false);
  useEffect(() => {
    const status = params.get("status");
    if (!status || told.current) return;
    told.current = true;
    const provider = params.get("connector") ?? "";
    const name = connectors.find((c) => c.provider === provider)?.name ?? provider;
    if (status in OUTCOMES) {
      const outcome = status as keyof typeof OUTCOMES;
      toast[OUTCOMES[outcome]](t(`connectors.${outcome}`, { name }));
    } else {
      const reason = params.get("reason") ?? "";
      toast.danger(t("connectors.installFailed", { name }), t(`connectors.reason.${REASONS.has(reason) ? (reason as "failed") : "failed"}`));
    }
    setParams({}, { replace: true });
  }, [params, setParams, connectors, t, toast]);
}

function ConnectorCard({ connector, compact }: { connector: ConnectorSummary; compact?: boolean }) {
  const { t, locale } = useI18n();
  const status = connectorStatus(connector);
  const description = localized(connector.description, locale);
  return (
    <Link to={connector.provider} className="dt-connector-card" data-testid={`connector-${connector.provider}`}>
      <span className="dt-connector-card__head">
        <ConnectorLogo connector={connector} />
        <span className="dt-connector-card__text">
          <span className="dt-connector-card__name">{connector.name}</span>
          {status && (
            <span className="dt-connector-card__status" data-tone={status.tone}>
              {t(status.key)}
            </span>
          )}
        </span>
      </span>
      {!compact && description && <span className="dt-connector-card__desc">{description}</span>}
    </Link>
  );
}

function Group({ title, connectors, compact = false }: { title: string; connectors: ConnectorSummary[]; compact?: boolean }) {
  if (connectors.length === 0) return null;
  return (
    <section className="dt-connector-group" aria-label={title}>
      <h2 className="dt-connector-group__title">{title}</h2>
      <div className="dt-connector-grid">
        {connectors.map((c) => (
          <ConnectorCard key={c.provider} connector={c} compact={compact} />
        ))}
      </div>
    </section>
  );
}

function Connectors({ connectors, canManage }: { connectors: ConnectorSummary[]; canManage: boolean }) {
  const { t } = useI18n();
  useInstallOutcome(connectors);
  return (
    <>
      {!canManage && <Callout tone="info">{t("connectors.readOnly")}</Callout>}
      {connectors.length === 0 && <EmptyState title={t("connectors.empty")} icon={<ConnectorIcon size={18} />} />}
      <Group title={t("connectors.status.enabled")} connectors={connectors.filter((c) => c.installation)} compact />
      <Group title={t("connectors.group.notInstalled")} connectors={connectors.filter((c) => !c.installation)} />
    </>
  );
}

/** One group of platform cards: logo, name, a word of status, two lines of description. */
function ConnectorGridSkeleton() {
  return (
    <Loading>
      <section className="dt-connector-group">
        <h2 className="dt-connector-group__title">
          <Skeleton width={96} />
        </h2>
        <div className="dt-connector-grid">
          {times(4).map((i) => (
            <div key={i} className="dt-connector-card" aria-hidden="true">
              <span className="dt-connector-card__head">
                <Skeleton shape="block" width={40} height={40} />
                <span className="dt-connector-card__text" style={{ flex: 1 }}>
                  <span className="dt-connector-card__name">
                    <Skeleton width="56%" />
                  </span>
                  <span className="dt-connector-card__status">
                    <Skeleton width="36%" />
                  </span>
                </span>
              </span>
              <span className="dt-connector-card__desc">
                <Skeleton width="100%" />
                <Skeleton width="70%" />
              </span>
            </div>
          ))}
        </div>
      </section>
    </Loading>
  );
}

export function ConnectorsSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("connectors.title")} />
      <ConnectorGridSkeleton />
    </>
  );
}

export function ConnectorsRoute() {
  const { data } = useLoaderData<typeof connectorsLoader>();
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("connectors.title")} />
      <Deferred data={data} fallback={<ConnectorGridSkeleton />}>
        {({ connectors, canManage }) => <Connectors connectors={connectors} canManage={canManage} />}
      </Deferred>
    </>
  );
}
